/**
 * Book-level timeline beats + timing issues (desktop-v2).
 * Source of truth: `book.metadata.timelineBeats` + `book.metadata.timelineIssues`.
 * Chapter `tabData.timeline` is a derived summary list.
 */

export type TimelineDatePrecision = "exact" | "approximate" | "relative" | "unknown";

export type TimelineBeatKind = "plot" | "action" | "character";

export type TimelineNarrativePosition = "present" | "flashback" | "foreshadow" | "recalled";

export type TimelineIssueKind =
  | "contradiction"
  | "chronology_conflict"
  | "under_specified"
  | "compressed"
  | "gap";

export type TimelineIssueSeverity = "info" | "warn" | "problem";

export type BookTimelineBeat = {
  id: string;
  /** Lasting beat identity — what changed for the book. */
  summary: string;
  /** Time reference as written in the text. */
  when: string;
  /** ISO-ish / ordinal key for chronological graph order; empty when unknown. */
  sortKey: string;
  datePrecision: TimelineDatePrecision;
  beatKind: TimelineBeatKind;
  narrativePosition: TimelineNarrativePosition;
  /** Primary chapter for this beat. */
  chapterId: string;
  /** All chapters this beat spans (whole-book). */
  chapterIds: string[];
  /** Chapter-local detail (scene coloring). */
  note?: string;
  /** Short grounded quote or paraphrase. */
  evidence?: string;
  updatedAt: string;
};

export type TimelineTimingIssue = {
  id: string;
  kind: TimelineIssueKind;
  severity: TimelineIssueSeverity;
  summary: string;
  evidence: string;
  beatIds: string[];
  chapterIds: string[];
  suggestion?: string;
  updatedAt: string;
};

/** Author-removed beat with reason — informs future extract. */
export type TimelineRemovalRecord = {
  id: string;
  beatId?: string;
  summary: string;
  when?: string;
  reason: string;
  removedAt: string;
};

export const TIMELINE_DATE_PRECISIONS: readonly TimelineDatePrecision[] = [
  "exact",
  "approximate",
  "relative",
  "unknown",
] as const;

export const TIMELINE_BEAT_KINDS: readonly TimelineBeatKind[] = [
  "plot",
  "action",
  "character",
] as const;

export const TIMELINE_NARRATIVE_POSITIONS: readonly TimelineNarrativePosition[] = [
  "present",
  "flashback",
  "foreshadow",
  "recalled",
] as const;

export const TIMELINE_ISSUE_KINDS: readonly TimelineIssueKind[] = [
  "contradiction",
  "chronology_conflict",
  "under_specified",
  "compressed",
  "gap",
] as const;

export const TIMELINE_ISSUE_SEVERITIES: readonly TimelineIssueSeverity[] = [
  "info",
  "warn",
  "problem",
] as const;

export const TIMELINE_BEAT_KIND_LABELS: Record<TimelineBeatKind, string> = {
  plot: "Plot",
  action: "Action",
  character: "Character",
};

export const TIMELINE_ISSUE_KIND_LABELS: Record<TimelineIssueKind, string> = {
  contradiction: "Contradiction",
  chronology_conflict: "Chronology conflict",
  under_specified: "Under-described",
  compressed: "Compressed time",
  gap: "Time gap",
};

export const TIMELINE_ISSUE_SEVERITY_LABELS: Record<TimelineIssueSeverity, string> = {
  info: "Info",
  warn: "Warning",
  problem: "Problem",
};

export function generateTimelineBeatId(): string {
  return `beat-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateTimelineIssueId(): string {
  return `tissue-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateTimelineRemovalId(): string {
  return `beat-removal-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeSummaryKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function asString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function normalizeDatePrecision(raw: unknown): TimelineDatePrecision {
  const v = asString(raw).toLowerCase();
  if (v === "exact" || v === "approximate" || v === "relative" || v === "unknown") return v;
  return "unknown";
}

function normalizeBeatKind(raw: unknown): TimelineBeatKind {
  const v = asString(raw).toLowerCase();
  if (v === "plot" || v === "action" || v === "character") return v;
  return "plot";
}

function normalizeNarrativePosition(raw: unknown): TimelineNarrativePosition {
  const v = asString(raw).toLowerCase();
  if (v === "present" || v === "flashback" || v === "foreshadow" || v === "recalled") return v;
  return "present";
}

function normalizeIssueKind(raw: unknown): TimelineIssueKind {
  const v = asString(raw).toLowerCase().replace(/-/g, "_");
  if (
    v === "contradiction" ||
    v === "chronology_conflict" ||
    v === "under_specified" ||
    v === "compressed" ||
    v === "gap"
  ) {
    return v;
  }
  return "under_specified";
}

function normalizeIssueSeverity(raw: unknown): TimelineIssueSeverity {
  const v = asString(raw).toLowerCase();
  if (v === "info" || v === "warn" || v === "problem") return v;
  return "warn";
}

export function isPlausibleTimelineBeatSummary(summary: string): boolean {
  const trimmed = summary.trim();
  if (!trimmed || trimmed.length < 3) return false;
  if (!/[a-zA-Z]/.test(trimmed)) return false;
  return true;
}

export function normalizeBookTimelineBeat(raw: unknown): BookTimelineBeat | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const summary = asString(rec.summary) || asString(rec.description) || asString(rec.event);
  if (!summary || !isPlausibleTimelineBeatSummary(summary)) return null;

  const chapterId =
    asString(rec.chapterId) ||
    (Array.isArray(rec.chapterIds) && typeof rec.chapterIds[0] === "string"
      ? String(rec.chapterIds[0]).trim()
      : "");
  const chapterIdsRaw = Array.isArray(rec.chapterIds)
    ? rec.chapterIds.filter((id): id is string => typeof id === "string").map((id) => id.trim())
    : [];
  const chapterIds = Array.from(
    new Set([...(chapterId ? [chapterId] : []), ...chapterIdsRaw].filter(Boolean))
  );
  const primaryChapterId = chapterIds[0] ?? chapterId;

  return {
    id: asString(rec.id) || generateTimelineBeatId(),
    summary,
    when: asString(rec.when) || asString(rec.date),
    sortKey: asString(rec.sortKey) || asString(rec.sortDate),
    datePrecision: normalizeDatePrecision(rec.datePrecision ?? rec.dateType),
    beatKind: normalizeBeatKind(rec.beatKind ?? rec.kind),
    narrativePosition: normalizeNarrativePosition(
      rec.narrativePosition ?? rec.eventType
    ),
    chapterId: primaryChapterId,
    chapterIds,
    note: asString(rec.note) || undefined,
    evidence: asString(rec.evidence) || undefined,
    updatedAt: asString(rec.updatedAt) || new Date().toISOString(),
  };
}

export function normalizeTimelineBeatRoster(raw: unknown): BookTimelineBeat[] {
  if (!Array.isArray(raw)) return [];
  const out: BookTimelineBeat[] = [];
  const seenIds = new Set<string>();
  for (const item of raw) {
    const beat = normalizeBookTimelineBeat(item);
    if (!beat) continue;
    if (seenIds.has(beat.id)) {
      beat.id = generateTimelineBeatId();
    }
    seenIds.add(beat.id);
    out.push(beat);
  }
  return sortBeatsChronologically(out);
}

export function normalizeTimelineTimingIssue(raw: unknown): TimelineTimingIssue | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const summary = asString(rec.summary);
  if (!summary) return null;
  return {
    id: asString(rec.id) || generateTimelineIssueId(),
    kind: normalizeIssueKind(rec.kind),
    severity: normalizeIssueSeverity(rec.severity),
    summary,
    evidence: asString(rec.evidence),
    beatIds: Array.isArray(rec.beatIds)
      ? rec.beatIds.filter((id): id is string => typeof id === "string").map((id) => id.trim())
      : [],
    chapterIds: Array.isArray(rec.chapterIds)
      ? rec.chapterIds.filter((id): id is string => typeof id === "string").map((id) => id.trim())
      : [],
    suggestion: asString(rec.suggestion) || undefined,
    updatedAt: asString(rec.updatedAt) || new Date().toISOString(),
  };
}

export function normalizeTimelineTimingIssues(raw: unknown): TimelineTimingIssue[] {
  if (!Array.isArray(raw)) return [];
  const out: TimelineTimingIssue[] = [];
  for (const item of raw) {
    const issue = normalizeTimelineTimingIssue(item);
    if (issue) out.push(issue);
  }
  return sortIssuesBySeverity(out);
}

export function normalizeTimelineRemoval(raw: unknown): TimelineRemovalRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const summary = asString(rec.summary);
  const reason = asString(rec.reason);
  if (!summary || !reason) return null;
  return {
    id: asString(rec.id) || generateTimelineRemovalId(),
    beatId: asString(rec.beatId) || undefined,
    summary,
    when: asString(rec.when) || undefined,
    reason,
    removedAt: asString(rec.removedAt) || new Date().toISOString(),
  };
}

export function normalizeTimelineRemovals(raw: unknown): TimelineRemovalRecord[] {
  if (!Array.isArray(raw)) return [];
  const out: TimelineRemovalRecord[] = [];
  for (const item of raw) {
    const record = normalizeTimelineRemoval(item);
    if (record) out.push(record);
  }
  return out.sort((a, b) => b.removedAt.localeCompare(a.removedAt));
}

/** Prefer chronological sortKey; fall back to chapter order index then summary. */
export function sortBeatsChronologically(
  beats: BookTimelineBeat[],
  chapterOrder?: Map<string, number>
): BookTimelineBeat[] {
  return [...beats].sort((a, b) => {
    const aKey = a.sortKey.trim();
    const bKey = b.sortKey.trim();
    if (aKey && bKey && aKey !== bKey) return aKey.localeCompare(bKey);
    if (aKey && !bKey) return -1;
    if (!aKey && bKey) return 1;
    if (chapterOrder) {
      const aOrd = chapterOrder.get(a.chapterId) ?? Number.MAX_SAFE_INTEGER;
      const bOrd = chapterOrder.get(b.chapterId) ?? Number.MAX_SAFE_INTEGER;
      if (aOrd !== bOrd) return aOrd - bOrd;
    }
    return a.summary.localeCompare(b.summary, undefined, { sensitivity: "base" });
  });
}

/** Story / spine order: chapter order then summary. */
export function sortBeatsByStoryOrder(
  beats: BookTimelineBeat[],
  chapterOrder: Map<string, number>
): BookTimelineBeat[] {
  return [...beats].sort((a, b) => {
    const aOrd = chapterOrder.get(a.chapterId) ?? Number.MAX_SAFE_INTEGER;
    const bOrd = chapterOrder.get(b.chapterId) ?? Number.MAX_SAFE_INTEGER;
    if (aOrd !== bOrd) return aOrd - bOrd;
    const aKey = a.sortKey.trim();
    const bKey = b.sortKey.trim();
    if (aKey && bKey && aKey !== bKey) return aKey.localeCompare(bKey);
    return a.summary.localeCompare(b.summary, undefined, { sensitivity: "base" });
  });
}

export function sortIssuesBySeverity(issues: TimelineTimingIssue[]): TimelineTimingIssue[] {
  const rank: Record<TimelineIssueSeverity, number> = { problem: 0, warn: 1, info: 2 };
  return [...issues].sort((a, b) => {
    const severityDiff = rank[a.severity] - rank[b.severity];
    if (severityDiff !== 0) return severityDiff;
    return a.summary.localeCompare(b.summary, undefined, { sensitivity: "base" });
  });
}

export function findBeatBySummary(
  roster: BookTimelineBeat[],
  summary: string
): BookTimelineBeat | undefined {
  const key = normalizeSummaryKey(summary);
  if (!key) return undefined;
  const exact = roster.find((beat) => normalizeSummaryKey(beat.summary) === key);
  if (exact) return exact;
  // Soft match: one summary contains the other when both are substantial.
  if (key.length < 12) return undefined;
  return roster.find((beat) => {
    const other = normalizeSummaryKey(beat.summary);
    if (other.length < 12) return false;
    return other.includes(key) || key.includes(other);
  });
}

export function isBeatSummaryRemoved(
  removals: TimelineRemovalRecord[],
  summary: string
): boolean {
  const key = normalizeSummaryKey(summary);
  if (!key) return false;
  return removals.some((record) => normalizeSummaryKey(record.summary) === key);
}

export type ExtractedTimelineBeatPayload = {
  summary: string;
  when?: string;
  sortKey?: string;
  datePrecision?: string;
  beatKind?: string;
  narrativePosition?: string;
  note?: string;
  evidence?: string;
  chapterIds?: string[];
};

export type ExtractedTimelineIssuePayload = {
  kind?: string;
  severity?: string;
  summary: string;
  evidence?: string;
  beatSummaries?: string[];
  chapterIds?: string[];
  suggestion?: string;
};

export function mergeExtractedBeatsIntoRoster(params: {
  roster: BookTimelineBeat[];
  extracted: ExtractedTimelineBeatPayload[];
  chapterId: string;
  removals?: TimelineRemovalRecord[];
  nowIso?: string;
}): BookTimelineBeat[] {
  const now = params.nowIso ?? new Date().toISOString();
  const removals = params.removals ?? [];
  let next = [...params.roster];

  for (const item of params.extracted) {
    const summary = asString(item.summary);
    if (!summary || !isPlausibleTimelineBeatSummary(summary)) continue;
    if (isBeatSummaryRemoved(removals, summary)) continue;

    const when = asString(item.when);
    const sortKey = asString(item.sortKey);
    const datePrecision = normalizeDatePrecision(item.datePrecision);
    const beatKind = normalizeBeatKind(item.beatKind);
    const narrativePosition = normalizeNarrativePosition(item.narrativePosition);
    const note = asString(item.note) || undefined;
    const evidence = asString(item.evidence) || undefined;

    const existing = findBeatBySummary(next, summary);
    if (existing) {
      const chapterIds = Array.from(
        new Set([...existing.chapterIds, params.chapterId].filter(Boolean))
      );
      next = next.map((beat) =>
        beat.id === existing.id
          ? {
              ...beat,
              // Prefer lasting summary; only fill when empty.
              summary: beat.summary.trim() || summary,
              when: when || beat.when,
              sortKey: sortKey || beat.sortKey,
              datePrecision:
                datePrecision !== "unknown" ? datePrecision : beat.datePrecision,
              beatKind,
              narrativePosition,
              chapterId: beat.chapterId || params.chapterId,
              chapterIds,
              note: note ?? beat.note,
              evidence: evidence ?? beat.evidence,
              updatedAt: now,
            }
          : beat
      );
    } else {
      next.push({
        id: generateTimelineBeatId(),
        summary,
        when,
        sortKey,
        datePrecision,
        beatKind,
        narrativePosition,
        chapterId: params.chapterId,
        chapterIds: [params.chapterId],
        note,
        evidence,
        updatedAt: now,
      });
    }
  }

  return sortBeatsChronologically(next);
}

/**
 * Merge a whole-book timeline pass. Holistic summary/when/sortKey are authoritative.
 * Existing chapter notes are preserved when the beat matches.
 */
export function mergeHolisticBeatsIntoRoster(params: {
  roster: BookTimelineBeat[];
  extracted: ExtractedTimelineBeatPayload[];
  validChapterIds: string[];
  removals?: TimelineRemovalRecord[];
  nowIso?: string;
}): BookTimelineBeat[] {
  const now = params.nowIso ?? new Date().toISOString();
  const validIds = new Set(params.validChapterIds);
  const removals = params.removals ?? [];
  let next = [...params.roster];

  for (const item of params.extracted) {
    const summary = asString(item.summary);
    if (!summary || !isPlausibleTimelineBeatSummary(summary)) continue;
    if (isBeatSummaryRemoved(removals, summary)) continue;

    const when = asString(item.when);
    const sortKey = asString(item.sortKey);
    const datePrecision = normalizeDatePrecision(item.datePrecision);
    const beatKind = normalizeBeatKind(item.beatKind);
    const narrativePosition = normalizeNarrativePosition(item.narrativePosition);
    const evidence = asString(item.evidence) || undefined;
    const chapterIds = Array.from(
      new Set(
        (Array.isArray(item.chapterIds) ? item.chapterIds : [])
          .filter((id): id is string => typeof id === "string")
          .map((id) => id.trim())
          .filter((id) => validIds.has(id))
      )
    );
    const primaryChapterId = chapterIds[0] ?? "";

    const existing = findBeatBySummary(next, summary);
    if (existing) {
      const mergedChapterIds = Array.from(
        new Set([...existing.chapterIds, ...chapterIds].filter((id) => validIds.has(id)))
      );
      next = next.map((beat) =>
        beat.id === existing.id
          ? {
              ...beat,
              summary: summary || beat.summary,
              when: when || beat.when,
              sortKey: sortKey || beat.sortKey,
              datePrecision:
                datePrecision !== "unknown" ? datePrecision : beat.datePrecision,
              beatKind,
              narrativePosition,
              chapterId: primaryChapterId || beat.chapterId,
              chapterIds: mergedChapterIds.length ? mergedChapterIds : beat.chapterIds,
              note: beat.note,
              evidence: evidence ?? beat.evidence,
              updatedAt: now,
            }
          : beat
      );
    } else {
      next.push({
        id: generateTimelineBeatId(),
        summary,
        when,
        sortKey,
        datePrecision,
        beatKind,
        narrativePosition,
        chapterId: primaryChapterId,
        chapterIds,
        evidence,
        updatedAt: now,
      });
    }
  }

  return sortBeatsChronologically(next);
}

export function mergeExtractedIssuesIntoRoster(params: {
  issues: TimelineTimingIssue[];
  extracted: ExtractedTimelineIssuePayload[];
  beats: BookTimelineBeat[];
  replace?: boolean;
  nowIso?: string;
}): TimelineTimingIssue[] {
  const now = params.nowIso ?? new Date().toISOString();
  const base = params.replace ? [] : [...params.issues];
  const next = [...base];

  for (const item of params.extracted) {
    const summary = asString(item.summary);
    if (!summary) continue;
    const beatSummaries = Array.isArray(item.beatSummaries)
      ? item.beatSummaries.filter((s): s is string => typeof s === "string")
      : [];
    const beatIds = beatSummaries
      .map((s) => findBeatBySummary(params.beats, s)?.id)
      .filter((id): id is string => Boolean(id));
    const chapterIds = Array.isArray(item.chapterIds)
      ? item.chapterIds.filter((id): id is string => typeof id === "string").map((id) => id.trim())
      : [];

    next.push({
      id: generateTimelineIssueId(),
      kind: normalizeIssueKind(item.kind),
      severity: normalizeIssueSeverity(item.severity),
      summary,
      evidence: asString(item.evidence),
      beatIds,
      chapterIds,
      suggestion: asString(item.suggestion) || undefined,
      updatedAt: now,
    });
  }

  return sortIssuesBySeverity(next);
}

/** Derive newline beat summaries for a chapter’s Timeline tab. */
export function chapterTimelineTabText(roster: BookTimelineBeat[], chapterId: string): string {
  return beatsInChapter(roster, chapterId)
    .map((beat) => {
      const when = beat.when.trim();
      return when ? `${when} — ${beat.summary}` : beat.summary;
    })
    .join("\n");
}

export function syncAllChapterTimelineTabs(
  chapters: Array<{ id: string; tabData: { timeline: string } }>,
  roster: BookTimelineBeat[]
): void {
  for (const chapter of chapters) {
    chapter.tabData.timeline = chapterTimelineTabText(roster, chapter.id);
  }
}

export function beatsInChapter(roster: BookTimelineBeat[], chapterId: string): BookTimelineBeat[] {
  return sortBeatsChronologically(
    roster.filter(
      (beat) => beat.chapterId === chapterId || beat.chapterIds.includes(chapterId)
    )
  );
}

export function getTimelineBeatsFromBookMetadata(
  metadata: Record<string, unknown> | undefined | null
): BookTimelineBeat[] {
  if (!metadata) return [];
  return normalizeTimelineBeatRoster(metadata.timelineBeats);
}

export function getTimelineIssuesFromBookMetadata(
  metadata: Record<string, unknown> | undefined | null
): TimelineTimingIssue[] {
  if (!metadata) return [];
  return normalizeTimelineTimingIssues(metadata.timelineIssues);
}

export function getTimelineRemovalsFromMetadata(
  metadata: Record<string, unknown> | undefined | null
): TimelineRemovalRecord[] {
  if (!metadata) return [];
  return normalizeTimelineRemovals(metadata.timelineRemovals);
}

export function createTimelineRemovalRecord(
  beat: Pick<BookTimelineBeat, "id" | "summary" | "when">,
  reason: string,
  removedAt = new Date().toISOString()
): TimelineRemovalRecord {
  return {
    id: generateTimelineRemovalId(),
    beatId: beat.id,
    summary: beat.summary.trim(),
    when: beat.when.trim() || undefined,
    reason: reason.trim(),
    removedAt,
  };
}

export function withTimelineInMetadata(
  metadata: Record<string, unknown>,
  beats: BookTimelineBeat[],
  issues?: TimelineTimingIssue[],
  removals?: TimelineRemovalRecord[]
): Record<string, unknown> {
  const next: Record<string, unknown> = {
    ...metadata,
    timelineBeats: sortBeatsChronologically(beats),
  };
  if (issues !== undefined) {
    next.timelineIssues = sortIssuesBySeverity(issues);
  } else if (Array.isArray(metadata.timelineIssues)) {
    next.timelineIssues = normalizeTimelineTimingIssues(metadata.timelineIssues);
  }
  if (removals !== undefined) {
    next.timelineRemovals = normalizeTimelineRemovals(removals);
  } else if (Array.isArray(metadata.timelineRemovals)) {
    next.timelineRemovals = normalizeTimelineRemovals(metadata.timelineRemovals);
  }
  return next;
}

export function withTimelineRemovalsInMetadata(
  metadata: Record<string, unknown>,
  removals: TimelineRemovalRecord[]
): Record<string, unknown> {
  return {
    ...metadata,
    timelineRemovals: normalizeTimelineRemovals(removals),
  };
}

export function formatTimelineRemovalsForExtractPrompt(removals: TimelineRemovalRecord[]): string {
  if (!removals.length) return "";
  return removals
    .slice(0, 40)
    .map((record) => {
      const when = record.when ? ` (${record.when})` : "";
      return `- "${record.summary}"${when}: ${record.reason}`;
    })
    .join("\n");
}

/** Seed beat stubs from chapter tab timeline lines when roster is empty. */
export function seedBeatsFromChapterTabs(
  chapters: Array<{ id: string; order?: number; tabData?: { timeline?: string } }>,
  removals: TimelineRemovalRecord[] = []
): BookTimelineBeat[] {
  const now = new Date().toISOString();
  const byKey = new Map<string, BookTimelineBeat>();

  const ordered = [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const chapter of ordered) {
    const lines = String(chapter.tabData?.timeline ?? "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    for (const line of lines) {
      let when = "";
      let summary = line;
      const dashMatch = line.match(/^(.+?)\s+[—–-]\s+(.+)$/);
      if (dashMatch) {
        when = dashMatch[1]!.trim();
        summary = dashMatch[2]!.trim();
      }
      if (!isPlausibleTimelineBeatSummary(summary)) continue;
      if (isBeatSummaryRemoved(removals, summary)) continue;
      const key = normalizeSummaryKey(summary);
      if (!key) continue;
      const existing = byKey.get(key);
      if (existing) {
        if (!existing.chapterIds.includes(chapter.id)) {
          existing.chapterIds.push(chapter.id);
        }
      } else {
        byKey.set(key, {
          id: generateTimelineBeatId(),
          summary,
          when,
          sortKey: "",
          datePrecision: when ? "relative" : "unknown",
          beatKind: "plot",
          narrativePosition: "present",
          chapterId: chapter.id,
          chapterIds: [chapter.id],
          updatedAt: now,
        });
      }
    }
  }
  return sortBeatsChronologically(Array.from(byKey.values()));
}

/** Load beats from extracted/timeline.json rollup (structured or legacy { event }). */
export function rosterFromExtractedTimelineJson(raw: unknown): BookTimelineBeat[] {
  if (!Array.isArray(raw)) return [];
  const mapped = raw.map((item) => {
    if (!item || typeof item !== "object") return item;
    const rec = item as Record<string, unknown>;
    if (asString(rec.summary) || asString(rec.description)) return item;
    if (asString(rec.event)) {
      return { ...rec, summary: asString(rec.event) };
    }
    return item;
  });
  return normalizeTimelineBeatRoster(mapped);
}

export function issuesForBeat(
  issues: TimelineTimingIssue[],
  beatId: string
): TimelineTimingIssue[] {
  return issues.filter((issue) => issue.beatIds.includes(beatId));
}

export function problematicIssues(issues: TimelineTimingIssue[]): TimelineTimingIssue[] {
  return issues.filter(
    (issue) =>
      issue.kind === "contradiction" ||
      issue.kind === "chronology_conflict" ||
      issue.kind === "compressed" ||
      issue.severity === "problem"
  );
}

export function underDescribedIssues(issues: TimelineTimingIssue[]): TimelineTimingIssue[] {
  return issues.filter(
    (issue) => issue.kind === "under_specified" || issue.kind === "gap"
  );
}
